
import { Tool } from "@/hooks/use-canvas-setup";
import { cn } from "@/lib/utils";
import { FileUploadInput } from "@/components/FileUploadInput";
import { ToolbarDescription, getToolDescription } from "./ToolbarDescription";
import { ToolbarVisibilityToggle } from "./ToolbarVisibilityToggle";
import { MainToolbarProps } from "./types";
import { useLayerManagement } from "@/hooks/use-layer-management";
import { 
  Plus, MousePointer, Layers, PenLine, Ruler,
  Edit, Save, Shapes, WandSparkles, Loader2, AlertCircle,
  Eraser
} from "lucide-react";
import { DrawTools } from "./sections/DrawTools";
import { MeasureTools } from "./sections/MeasureTools";
import { LayerManagementMenu } from "./LayerManagementMenu";
import { AddTools } from "./sections/AddTools";
import { ShapesTools } from "./sections/ShapesTools";
import { SaveTools } from "./sections/SaveTools";
import { EditDeleteTools } from "./sections/EditDeleteTools";
import { useState, useEffect } from "react";
import { useAnalysis } from "@/hooks/use-analysis";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const MainToolbar = ({
  canvas,
  activeTool,
  onToolToggle,
  hasSelection,
  onUndo,
  onRedo,
  onDelete,
  onClear,
  projectTitle,
  onTitleChange,
  currentDrawingId,
  onDrawingSaved,
  isToolbarVisible,
  setIsToolbarVisible,
  onRefreshDrawings,
  rawMode,
  onRawModeChange,
  showPricing = false,
  canUndo,
  canRedo,
  onResetAnalysis,
  isAnalysisPanelOpen,
  setIsAnalysisPanelOpen,
  layerManagement,
}: MainToolbarProps) => {
  const [highlightAddButton, setHighlightAddButton] = useState(false);
  
  const { activeScenario, isAnalyzing, error, analysisResult } = useAnalysis(canvas, currentDrawingId);

  useEffect(() => {
    if (highlightAddButton) {
      const timer = setTimeout(() => {
        setHighlightAddButton(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [highlightAddButton]);

  const toolDescription = getToolDescription(activeTool);
  const isDrawingTool = activeTool === "draw" || activeTool === "incision" || activeTool === "scalpel";

  if (showPricing) return null;
  
  const handleAnalysisClick = () => {
    setIsAnalysisPanelOpen(!isAnalysisPanelOpen);
  };

  return (
    <div className="absolute left-1/2 -translate-x-1/2 bottom-3 z-40" data-toolbar>
      <div className={cn(
        "origin-top shadow-[0_4px_20px_rgba(0,0,0,0.08)] rounded-full",
        "transition-all duration-300",
        isToolbarVisible 
          ? "scale-100 opacity-100 translate-y-0" 
          : "scale-95 opacity-0 translate-y-4 pointer-events-none"
      )}>
        <ToolbarDescription
          toolDescription={toolDescription}
          isDrawingTool={isDrawingTool}
          rawMode={rawMode}
          onRawModeChange={onRawModeChange}
        />

        <div className="modern-toolbar flex items-center justify-center gap-4 px-4"> {/* Updated to have equal spacing */}
          <AddTools 
            canvas={canvas}
            onToolChange={onToolToggle}
            icon={<Plus className="h-4 w-4" />}
            label="Add"
            highlight={highlightAddButton}
          />

          <ShapesTools
            canvas={canvas}
            onToolToggle={onToolToggle}
            icon={<Shapes className="h-4 w-4" />}
            label="Shapes"
          />

          <DrawTools 
            canvas={canvas}
            currentTool={activeTool || ""}
            onToolChange={onToolToggle}
            icon={<PenLine className="h-4 w-4" />}
            label="Draw"
          />

          <div className="toolbar-item">
            <button 
              onClick={() => onToolToggle(activeTool === "transform" ? "" : "transform")}
              className={cn(
                "modern-toolbar-button",
                activeTool === "transform" && "active"
              )}
              aria-label="Select tool"
            >
              <MousePointer className="h-4 w-4" />
            </button>
          </div>

          <div className="toolbar-item">
            {layerManagement && (
              <LayerManagementMenu
                layers={layerManagement.layers}
                onToggleVisibility={layerManagement.toggleLayerVisibility}
                onRename={layerManagement.renameLayer}
                onBringForward={layerManagement.bringLayerForward}
                onSendBackward={layerManagement.sendLayerBackward}
                triggerContent={<Layers className="h-4 w-4" />}
              />
            )}
          </div>

          <MeasureTools
            activeTool={activeTool as "ruler" | "angle" | null}
            onToolToggle={onToolToggle}
            icon={<Ruler className="h-4 w-4" />}
            label="Measure"
          />

          <EditDeleteTools
            onUndo={onUndo}
            onRedo={onRedo}
            onDelete={onDelete}
            onClear={onClear}
            hasSelection={hasSelection}
            canUndo={canUndo}
            canRedo={canRedo}
            icon={<Eraser className="h-4 w-4" />}
            label="Edit & Delete"
          />

          <SaveTools
            canvas={canvas}
            onRefreshNeeded={onRefreshDrawings}
            title={projectTitle}
            onTitleChange={onTitleChange}
            currentDrawingId={currentDrawingId}
            onDrawingSaved={onDrawingSaved}
            icon={<Save className="h-4 w-4" />}
            label="Save"
          />

          <div className="ml-1">
            <FileUploadInput 
              canvas={canvas}
              onImageAdded={() => onToolToggle("transform")}
              onToolChange={onToolToggle}
            />
          </div>

          <div className="toolbar-item">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    onClick={handleAnalysisClick}
                    className={cn(
                      "modern-toolbar-button",
                      "bg-gradient-to-br from-indigo-400 via-blue-500 to-indigo-600 animate-gradient-xy",
                      "ring-2 ring-indigo-200/50",
                      isAnalyzing && "animate-pulse",
                      "shadow-md hover:shadow-lg transition-all duration-300",
                      "scale-110 hover:scale-125",
                      "relative overflow-hidden group"
                    )}
                    aria-label="Second Opinion"
                  >
                    {isAnalyzing ? (
                      <Loader2 className="h-4 w-4 text-white animate-spin" />
                    ) : error ? (
                      <AlertCircle className="h-4 w-4 text-white animate-pulse" strokeWidth={1.5} />
                    ) : (
                      <WandSparkles className="h-4 w-4 text-white animate-sparkle" strokeWidth={1.5} />
                    )}
                    
                    {analysisResult && !isAnalyzing && !error && (
                      <div className="absolute -top-1 -right-1">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-sm" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="font-medium bg-white/90 backdrop-blur-sm border border-white/50 shadow-lg">
                  Second Opinion
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      <ToolbarVisibilityToggle
        isToolbarVisible={isToolbarVisible}
        setIsToolbarVisible={setIsToolbarVisible}
        showPricing={showPricing}
      />
    </div>
  );
};
